import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import ClientProvider from "../../../modules/ClientProvider";
import { IAdvisor, IProject } from "../../../types/database";
import { DisplayBoxBase } from "../../../components/shared/StyledComponents";
import { Button, Stack, Typography, Box } from "@mui/material";
import { useAdvisor } from "../context/AdvisorContext";
import { Check } from "lucide-react";
import { theme } from "../../../theme/theme";
import { CalendlyScheduler, DateDisplay, MeetingDisplay, MeetingLinks } from "../../../features/meeting";
import { useDealContext } from "../../deal/context";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

export const OnboardingSetup = () => {

    const { getAccessTokenSilently } = useAuth0();
    const advisorHttpClient = ClientProvider.provideAdvisorClient();
    const [isSchedulerOpen, setIsSchedulerOpen] = useState<boolean>(false);
    const { advisor, loading, error, fetchAdvisor, updateAdvisor } = useAdvisor();
    const { dealContext } = useDealContext();
    const [isIntroSchedulerOpen, setIsIntroSchedulerOpen] = useState<boolean>(false);
    const [scheduler, setScheduler] = useState<any>(null);

    const fetchScheduler = async (type: "intro" | "demo") => {
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await advisorHttpClient.getScheduler(dealContext.project.tag, type, accessToken);
        if (data) {
            setScheduler(data.scheduler);
        }
    }

    const launchScheduler = async (type: "intro" | "demo") => {
        await fetchScheduler(type);
        setIsSchedulerOpen(true);
    }

    const handleMeetingCreated = () => {
        setIsSchedulerOpen(false);
        fetchAdvisor(dealContext.project.tag);
    }

    const introMeeting = advisor?.meetings?.find((meeting: any) => meeting?.scheduler?.type === "intro");
    const demoMeeting = advisor?.meetings?.find((meeting: any) => meeting?.scheduler?.type === "demo");

    if (!advisor) {
        return null;
    }

    return (
        <DisplayBoxBase className="grey" sx={{ flexDirection: "column", gap: "1rem", marginBottom: "40px", alignItems: "flex-start" }}>
            <Typography variant="h5" fontWeight="bold">Welcome to the {dealContext.project.name} advisor program</Typography>
            <Typography variant="body1">
                We're excited to have you on board. Just {!introMeeting && !demoMeeting ? "two more steps" : "one more step"} to complete your onboarding.
            </Typography>
            <Stack direction="row" spacing={2} mt={2} mb={4}>
                <DisplayBoxBase sx={{
                    flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: "10px", "& span.step": {
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        color: '#fff',
                        borderRadius: '50%',
                        backgroundColor: theme.palette.success.main,
                        width: '25px',
                        height: '25px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '10px'
                    }
                }}>
                    <Typography variant="body1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}><span className="step"><Check size={16} /></span>Provide Insights</Typography>
                    <Typography variant="body1">Initial feedback should take 10-15 minutes and can be completed online.</Typography>
                </DisplayBoxBase>
                <DisplayBoxBase sx={{
                    flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: "10px", "& span.step": {
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        color: '#fff',
                        borderRadius: '50%',
                        backgroundColor: introMeeting ? theme.palette.success.main : "#000",
                        width: '25px',
                        height: '25px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '10px'
                    }
                }}>
                    <Typography variant="body1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}><span className="step">{introMeeting ? <Check size={16} /> : "2"}</span>Meet your Sponsor</Typography>
                    <Typography variant="body1">Book a 15 minute call with your {dealContext.project.name} executive sponsor.</Typography>
                    {introMeeting ? (
                        <MeetingDisplay meeting={introMeeting} advisor={advisor} />
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', height: '100%', marginTop: "10px", width: "100%" }}>
                            <Button variant="contained" color="primary" onClick={() => launchScheduler("intro")}>Schedule a Meeting</Button>
                        </Box>
                    )}
                </DisplayBoxBase>
                <DisplayBoxBase sx={{
                    flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: "10px", "& span.step": {
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        color: '#fff',
                        borderRadius: '50%',
                        backgroundColor: demoMeeting ? theme.palette.success.main : "#000",
                        width: '25px',
                        height: '25px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '10px'
                    }
                }}>
                    <Typography variant="body1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}><span className="step">{demoMeeting ? <Check size={16} /> : "3"}</span>Get a Demo</Typography>
                    <Typography variant="body1">Schedule a 30 minute demo to learn more about {dealContext.project.name}.</Typography>
                    {demoMeeting ? (
                        <MeetingDisplay meeting={demoMeeting} advisor={advisor} />
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', height: '100%', marginTop: "10px", width: "100%" }}>
                            <Button variant="contained" color="primary" onClick={() => launchScheduler("demo")}>Schedule a Meeting</Button>
                        </Box>
                    )}
                </DisplayBoxBase>
            </Stack>
            {scheduler && <CalendlyScheduler project={dealContext.project} scheduler={scheduler} advisor={advisor} surveyLink={dealContext.surveyLink} onMeetingCreated={handleMeetingCreated} inline={false} open={isSchedulerOpen} setOpen={setIsSchedulerOpen} />}
        </DisplayBoxBase>
    );
}